import { SilenceChildComponent } from "@faire/deprecated-retailer-visitor-shared/components/Silence/SilenceChildComponent";
import { InviteTeamMemberSection } from "@faire/deprecated-retailer-visitor-shared/views/multi-user/Invite/InviteTeamMemberSection";
import { TOAST_AUTO_DISMISS_TIME_MS } from "@faire/deprecated-retailer-visitor-shared/views/multi-user/Share/consts";
import { Action, Core } from "@faire/slate/foundation/color";
import { Link } from "@faire/slate/icons/Link";
import { trackRetailerTeamManagementCopyLinkButtonClick } from "@faire/web-api--source/events/retailerTeamManagement/click/copyLinkButton";
import { trackRetailerTeamSharingInviteUserLeanFlowClick } from "@faire/web-api--source/events/retailerTeamSharing/click/inviteUserLeanFlow";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { IUserMilestone } from "@faire/web-api--source/indigofair/data/IUserMilestone";
import { ShareableItemType } from "@faire/web-api--source/indigofair/retailer/ShareableItemType";
import { copyTextToClipboard } from "@faire/web/common/copyTextToClipboard";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { optimizeImageUrlFillSquare } from "@faire/web/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web/common/localization";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Button } from "@faire/web/slate-deprecated/Button";
import { Column, HorizontalLine, Row } from "@faire/web/slate/Layout";
import { Modal } from "@faire/web/slate/Modal";
import { Spacer } from "@faire/web/slate/spacing";
import { createToast } from "@faire/web/slate/Toast/Toast";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";

interface IProps {
  modalType: ShareableItemType;
  image: IImage | undefined;
  name: string | undefined;
  subText: string | undefined;
  onClose: () => void;
  pageName: string;
  silenceGTMMilestone: IUserMilestone.RetailerMilestone;
  viewCountBeforeSilencingGTM: number;
  onInvite?: () => void;
}

const ITEM_IMAGE_SIZE = 96;

export const ShareWithTeamGTMModal: React.FC<IProps> = ({
  modalType,
  image,
  name,
  subText,
  onClose,
  silenceGTMMilestone,
  viewCountBeforeSilencingGTM,
  onInvite,
  pageName,
}) => {
  const isSSR = useIsSSR();
  const viewport = useViewport();
  const { strictLocalize } = useStrictLocalization();

  const createLinkCopiedToast = () => {
    createToast(
      <StrictLocalMsg
        id="e0OktD"
        defaultMessage="Link copied successfully"
        description={{
          text: "text for toast that lets user know that they have copied a link",
        }}
      />,
      {
        icon: "foundation:CheckOutline",
        timeout: TOAST_AUTO_DISMISS_TIME_MS,
      }
    );
  };

  const handleCopyLink = () => {
    trackRetailerTeamManagementCopyLinkButtonClick(pageName);

    const window = getWindowOrThrow();
    copyTextToClipboard(window.location.href);

    createLinkCopiedToast();
    onClose();
  };

  const inviteSectionDescription =
    modalType === ShareableItemType.PRODUCT
      ? strictLocalize({
          id: "ZxoAZW",
          defaultMessage:
            "Or invite a team member to join your account so they can view full product details.",
          description: {
            text: "text for modal that lets user know that they can invite a team member to the platform so they can view the product",
            img: "https://cdn.faire.com/fastly/07ae3e05c905f4558ad3feff55bdf01e15c66eb18c97d8ae099761d586a9b7fa.png",
          },
        })
      : strictLocalize({
          id: "anS3+b",
          defaultMessage:
            "Or invite a team member to join your account so they can view full brand details.",
          description: {
            text: "text for modal that lets user know that they can invite a team member to the platform so they can view the brand",
            img: "https://cdn.faire.com/fastly/57af2af0ea5beb49d2b4ab948bb4865c8ee927a156fed12efaf9bd1f81b247c8.png",
          },
        });

  return (
    <Modal
      padding="88px 24px 24px 24px"
      width={viewport.isTabletAndAbove ? "420px" : undefined}
      onClose={onClose}
      align="stretch"
      data-test-id="share-with-team-gtm-modal"
    >
      <Typography variant="pageHeaderSerifRegular">
        {modalType === ShareableItemType.PRODUCT ? (
          <StrictLocalMsg
            id="RPANM+"
            defaultMessage="Share product"
            description={{
              text: "title for modal that lets user know that they are sharing a product",
              img: "https://cdn.faire.com/fastly/07ae3e05c905f4558ad3feff55bdf01e15c66eb18c97d8ae099761d586a9b7fa.png",
            }}
          />
        ) : (
          <StrictLocalMsg
            id="s7lPKw"
            defaultMessage="Share brand"
            description={{
              text: "title for modal that lets user know that they are sharing a brand",
              img: "https://cdn.faire.com/fastly/57af2af0ea5beb49d2b4ab948bb4865c8ee927a156fed12efaf9bd1f81b247c8.png",
            }}
          />
        )}
      </Typography>
      <Spacer size="2x" />
      <Typography color={Action.text.hover}>
        {modalType === ShareableItemType.PRODUCT ? (
          <StrictLocalMsg
            id="vlpz3F"
            defaultMessage="Send a preview of this product to anyone."
            description={{
              text: "text for modal that lets user know that they are sharing a product",
              img: "https://cdn.faire.com/fastly/07ae3e05c905f4558ad3feff55bdf01e15c66eb18c97d8ae099761d586a9b7fa.png",
            }}
          />
        ) : (
          <StrictLocalMsg
            id="XhGWAo"
            defaultMessage="Send a preview of this brand to anyone."
            description={{
              text: "text for modal that lets user know that they are sharing a brand",
              img: "https://cdn.faire.com/fastly/57af2af0ea5beb49d2b4ab948bb4865c8ee927a156fed12efaf9bd1f81b247c8.png",
            }}
          />
        )}
      </Typography>
      <Spacer height="2x" />
      <Row align="center">
        <img
          src={optimizeImageUrlFillSquare(
            { url: image?.url },
            isSSR,
            ITEM_IMAGE_SIZE
          )}
          width={ITEM_IMAGE_SIZE}
          height={ITEM_IMAGE_SIZE}
        />
        <Spacer size="2x" />
        <Column>
          <Typography>{name}</Typography>
          <Spacer size={4} />
          <Typography color={Core.text.subdued}>{subText}</Typography>
        </Column>
      </Row>
      <Spacer height="2x" />
      <Row>
        <Button variant="tertiary" onClick={handleCopyLink}>
          <Link />
          <Spacer size={4} />
          <StrictLocalMsg
            id="eqg+27"
            defaultMessage="Copy preview link"
            description={{
              text: "text for button that lets user know that they can copy a share link",
              img: "https://cdn.faire.com/fastly/57af2af0ea5beb49d2b4ab948bb4865c8ee927a156fed12efaf9bd1f81b247c8.png",
            }}
          />
        </Button>
      </Row>

      <SilenceChildComponent
        milestone={silenceGTMMilestone}
        maxViewCount={viewCountBeforeSilencingGTM}
      >
        <Spacer height="3x" />
        <HorizontalLine />
        <Spacer height="3x" />
        <Typography>{inviteSectionDescription}</Typography>
        <Spacer height="2x" />
        <InviteTeamMemberSection
          onInviteSent={onInvite}
          onInviteButtonClick={() =>
            trackRetailerTeamSharingInviteUserLeanFlowClick(pageName)
          }
        />
      </SilenceChildComponent>
    </Modal>
  );
};
